import { Injectable, signal } from '@angular/core';
import { isInstalledAppMode } from '../analytics/device-context';

const LOCK_NAME = 'jiopc-app-instance';
const CHANNEL_NAME = 'jiopc-instance';

type InstanceMessage = { type: 'FOCUS' } | { type: 'TAKE_OVER' };

/**
 * Enforces a single running instance of the app across browser tabs/windows,
 * via the Web Locks API + BroadcastChannel. Consolidates Consumer's TWO
 * overlapping implementations (core/singleInstance.ts, used pre-render in
 * main.tsx, and core/components/SingleInstanceGuard.tsx, used inside
 * Providers — see documents/technical/00-core-infrastructure.md finding #2)
 * into one Angular service, keeping the more capable behavior: a duplicate
 * tab can ask the leader to focus, AND the duplicate can explicitly "take
 * over" (the leader releases its lock so the duplicate becomes primary).
 */
@Injectable({ providedIn: 'root' })
export class SingleInstanceService {
  readonly isDuplicate = signal<boolean | null>(null);

  private channel: BroadcastChannel | null = null;
  private lockResolver: (() => void) | null = null;
  private initialized = false;

  /** Call once, near app bootstrap. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    if (isInstalledAppMode()) {
      this.isDuplicate.set(false);
      return;
    }

    if (!('locks' in navigator)) {
      this.isDuplicate.set(false);
      return;
    }

    const channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel = channel;

    channel.onmessage = (event: MessageEvent<InstanceMessage>) => {
      if (event.data.type === 'FOCUS') {
        try {
          window.focus();
        } catch {
          /* ignore */
        }
      } else if (event.data.type === 'TAKE_OVER') {
        this.lockResolver?.();
        this.lockResolver = null;
        this.isDuplicate.set(true);
      }
    };

    navigator.locks.request(LOCK_NAME, { ifAvailable: true }, (lock) => {
      if (!lock) {
        this.isDuplicate.set(true);
        channel.postMessage({ type: 'FOCUS' } satisfies InstanceMessage);
        return undefined;
      }

      this.isDuplicate.set(false);
      return new Promise<void>((resolve) => {
        this.lockResolver = resolve;
      });
    });
  }

  /**
   * Relinquish this tab's participation in the single-instance lock. Used when
   * the tab turns out to be a gated browser session (e.g. a PWA-spawned tab
   * that resolved to `display-mode: browser`) that ran bootstrap before the
   * gate applied. Such a tab must not count as a running app instance —
   * otherwise the real PWA launching next is wrongly seen as a duplicate
   * ("already open"). Safe to call when nothing was acquired.
   */
  release(): void {
    // Resolving the held lock's promise releases the Web Lock; a duplicate tab
    // never held it (resolver is null), so this is a safe no-op there.
    this.lockResolver?.();
    this.lockResolver = null;
    this.channel?.close();
    this.channel = null;
    this.isDuplicate.set(null);
    // Permit a fresh init() later; harmless as continueBootstrap is one-shot.
    this.initialized = false;
  }

  /** User chose "Use Here Instead" — asks the current leader to release the lock. */
  takeOver(): void {
    this.channel?.postMessage({ type: 'TAKE_OVER' } satisfies InstanceMessage);
    this.isDuplicate.set(null);

    navigator.locks.request(LOCK_NAME, () => {
      this.isDuplicate.set(false);
      return new Promise<void>((resolve) => {
        this.lockResolver = resolve;
      });
    });
  }
}
